import { Component, EventEmitter, Output, Input } from '@angular/core';
import { NgForm } from '@angular/forms';
import { FormGroup } from '../form-group/form-group.class';
import { forkJoin } from 'rxjs';
import { FormControl } from '../form-control/form-control.component';
import { LodashUtilityService } from '@edassist/common';

/**
 * Angular NgForm maintains a flag, on the root FormGroup, that tracks if the form is submitted. However,
 * it does not pass that flag to nested FormGroups/FormControls. It also only propagates resets, to
 * nested FormGroup/FormControl, for validity (specifically pristine and touched).
 *
 * These two limitations are problematic because:
 * 	 1.) Many error messages should only show once the form is submitted. Not while the user is typing.
 */
@Component({
	selector: 'ea-ui-form',
	templateUrl: 'form.component.html',
	styleUrls: ['./form.component.scss']
})
export class FormComponent extends NgForm {
	@Output() readonly handleSubmit: EventEmitter<any> = new EventEmitter<any>();
	@Output() readonly handleSuccessUpdate: EventEmitter<void> = new EventEmitter<void>();
	@Output() readonly formError: EventEmitter<string[]> = new EventEmitter<string[]>();

	@Input() showRequiredIndicator = false;
	@Input() requireDirtySubmit = true; // So we can submit forms without any inputs / changes

	form: FormGroup;

	editing = false;
	submitted = false;

	submit = (submitCallbackData?: any): void => {
		this.propagateSubmit(true);

		if (this.pristine && this.requireDirtySubmit) {
			return;
		}

		if (this.invalid) {
			const errorFields = Object.keys(this.form.controls).filter((field) => this.form.controls[field].invalid);
			this.formError.emit(errorFields);

			return;
		}

		// For a form with no child-forms and a single ngSubmit on the parent form:
		if (this.handleSubmit.observers.length === 1) {
			this.handleSubmit.emit(this.form.getRawValue());

			return;
		}

		this.callChildFormGroupOnSubmitCallbacks(submitCallbackData);
	};

	edit = (): void => {
		this.propagateEdit(true);
	};

	cancel = (): void => {
		this.propagateCancel();
		this.clean();
	};

	private readonly callChildFormGroupOnSubmitCallbacks = (submitCallbackData?: any): void => {
		// Allows data components to update their values, to the new API response values, before the form attempts a reset
		forkJoin(
			this.getFormGroups()
				.filter((childFormGroup) => childFormGroup.dirty)
				.map((childForm) => {
					if (childForm.onlyDirtyFormControls) {
						return this.filterPristineControls(childForm);
					}

					return childForm;
				})
				.map((childFormGroupDirty) =>
					childFormGroupDirty.onSubmit(childFormGroupDirty.getRawValue(), submitCallbackData)
				)
		).subscribe((data: any) => {
			this.propagateSubmitSuccess(data[0]);
			// After successful update of form, some components needs refresh
			this.handleSuccessUpdate.emit();
			this.clean();
		});
	};

	private readonly clean = (): void => {
		this.propagateEdit(false);
		this.propagateSubmit(false);
		this.form.markAsPristine();
		this.form.markAsUntouched();
	};

	// Note: Impure function by design
	private readonly getFormGroups = (formGroups: Array<FormGroup> = [], nextControl = this.form): Array<FormGroup> => {
		this.forEachControl(nextControl, (control) => {
			if (control instanceof FormGroup) {
				formGroups.push(control);
				this.getFormGroups(formGroups, control);
			}
		});

		return formGroups;
	};

	private readonly propagateSubmitSuccess = (newValue, nextControl = this.form): void => {
		this.forEachControl(nextControl, (control) => {
			// We aren't always passing the form back from the submit observable so check for it before we try to access it
			// for any submit success code on the control we need.
			if (control instanceof FormControl && newValue && newValue[control.name]) {
				control.onSubmitSuccess(newValue[control.name]);
			}

			if (this.handleSubmit.observers.length === 1) {
				return;
			}

			if (control instanceof FormGroup) {
				this.propagateSubmitSuccess(newValue, control);
			}
		});
	};

	private readonly propagateCancel = (nextControl = this.form): void => {
		this.forEachControl(nextControl, (control) => {
			control.onReset();

			if (control instanceof FormGroup) {
				this.propagateCancel(control);
			}
		});
	};

	private readonly propagateSubmit = (value: boolean, nextControl = this.form): void => {
		this.submitted = value;
		this.forEachControl(nextControl, (control) => {
			control.submitted = value;

			if (control instanceof FormGroup) {
				this.propagateSubmit(value, control);
			}
		});
	};

	private readonly propagateEdit = (value: boolean, nextControl = this.form): void => {
		this.editing = value;
		this.forEachControl(nextControl, (control) => {
			control.editing = value;

			if (control instanceof FormGroup) {
				this.propagateEdit(value, control);
			}
		});
	};

	private readonly forEachControl = (control, callback): void => {
		Object.keys(control.controls).forEach((key) => {
			callback(control.controls[key]);
		});
	};

	private filterPristineControls(form: FormGroup): FormGroup {
		// Lodash's Clone Deep method was the only one that copied over validation / change states (pristine, valid, dirty, etc)
		// new FormGroup() on the existing one and using Object.assign or rest (...) operator does not copy the validation / changed states
		// of the form controls.
		// We want to clone to not alter the original form for subsequent submissions
		const copiedForm = LodashUtilityService.cloneDeep(form);
		this.forEachControl(copiedForm, (control) => {
			if (control instanceof FormControl && control.pristine) {
				copiedForm.removeControl(control.name);
			}
		});

		return copiedForm;
	}
}
